import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || categories[0].slug;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (slug: string) => {
    setActiveTab(slug);
    setSearchParams({ tab: slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCategory = categories.find((c) => c.slug === activeTab);
  const filteredArticles = articles.filter((a) => a.categoryId === activeCategory?.id);

  const pageMeta = {
    title: `分类浏览 - ${siteInfo.name}`,
    description: activeCategory?.description || siteInfo.description,
    keywords: `动漫本子分类,${activeCategory?.name || '同人志'},动漫收藏分类`,
    canonicalUrl: `${siteInfo.url}/category`,
    ogType: 'website',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${siteInfo.url}/category#collection`,
    url: `${siteInfo.url}/category`,
    name: `分类浏览 - ${siteInfo.name}`,
    description: activeCategory?.description || siteInfo.description,
    isPartOf: { '@id': `${siteInfo.url}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${siteInfo.url}/category` },
      ],
    },
  };

  return (
    <>
      <SEOHead meta={pageMeta} />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '分类浏览' }]} />

        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                分类浏览
              </h1>
              <p className="text-sm text-foreground-600">按类别探索你感兴趣的收藏内容</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="内容分类">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={activeTab === cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={cat.icon}></i>
                    </div>
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>

            {activeCategory && (
              <div className="mb-8 bg-background-100 rounded-lg p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                    <i className={`${activeCategory.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-1.5">
                      {activeCategory.name}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {activeCategory.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/30 cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                        <span className="text-xs px-2.5 py-1 bg-accent-100 text-accent-800 rounded-full font-medium whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                        <span className="text-xs text-foreground-500">{article.author}</span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}